import { useState } from 'react'
import { PreviewableList } from '../../components/chat/input/context/previewable-list'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils'
import { ChevronDown } from 'lucide-react'

interface SearchAudioNotesToolProps {
  results?: Array<{
    id: string
    title: string
    summary?: string
    transcript?: string
  }>
  isLoading: boolean
  error?: string
  isSubmessage?: boolean
}

export function SearchAudioNotesTool({ results, isLoading, error, isSubmessage }: SearchAudioNotesToolProps) {
  const [isOpen, setIsOpen] = useState(false)

  // only show in submessages context after completion
  if (isLoading || !isSubmessage) {
    return null
  }

  if (error) {
    return (
      <div className="border-light-10 mb-3 flex flex-col rounded-2xl border">
        <div className="bg-light-5 flex min-h-11 flex-col justify-center rounded-2xl px-3 text-sm font-medium">
          <span>Failed to search audio notes</span>
        </div>
      </div>
    )
  }

  if (!results?.length) {
    return (
      <div className="border-light-10 mb-3 flex flex-col rounded-2xl border">
        <div className="bg-light-5 flex min-h-11 flex-col justify-center rounded-2xl px-4 py-4 text-sm font-medium">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-foreground font-semibold">No audio notes found</span>
          </div>
          <span className="text-foreground-placeholder text-xs">
            Note: Recent audio notes might take some time to be searchable.
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="border-light-10 flex flex-col rounded-2xl border">
      <div
        className={cn(
          'bg-light-5 hover:bg-light-10 cursor-pointer transition-colors',
          isOpen ? 'rounded-t-2xl' : 'rounded-2xl',
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex min-h-11 items-center justify-between px-3 py-2 text-sm font-medium">
          <div className="text-foreground flex items-center gap-1">
            <span>Highlight used </span>
            <span className="bg-light-10 m-0 rounded-md bg-opacity-10 p-1.5 py-0">Search Audio Notes</span>
            <span className="text-foreground-placeholder ml-1">
              • {results.length} result{results.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ChevronDown
            size={18}
            className={cn('text-foreground-placeholder transition-transform', isOpen && 'rotate-180')}
          />
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-b-2xl"
          >
            <div className="border-light-10 border-t">
              <PreviewableList results={results} disableScrollable />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
